import dayjs from 'dayjs';
import type { LeverageCampaign, LeveragePrizePool, LeverageTier } from './types';

export function getDefaultCampaign(): LeverageCampaign {
  return {
    id: 0,
    name: '',
    c1StartAt: 0,
    c1EndAt: 0,
    c1IsEnabled: false,
    c2StartAt: 0,
    c2EndAt: 0,
    c2IsEnabled: false,
    multiplier: 10,
    voucherCap: 500,
  };
}

export function getDefaultPool(): LeveragePrizePool {
  return {
    totalUsdc: '0',
    consumedUsdc: '0',
    updatedAt: 0,
  };
}

export function msToLocalInput(ms?: number | null): string {
  if (!ms || !Number.isFinite(ms)) return '';
  const parsed = dayjs(ms);
  if (!parsed.isValid()) return '';
  return parsed.format('YYYY-MM-DDTHH:mm');
}

export function localInputToMs(value: string): number {
  if (!value) return 0;
  const parsed = dayjs(value);
  if (!parsed.isValid()) return 0;
  return parsed.valueOf();
}

export function formatUsdc(value?: string | number | null): string {
  if (value === undefined || value === null || value === '') return '0';
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return new Intl.NumberFormat('en-US').format(num);
}

export function clampPct(num: number): number {
  if (!Number.isFinite(num)) return 0;
  return Math.max(0, Math.min(100, num));
}

export function getPoolPct(pool: LeveragePrizePool): number {
  const total = Number(pool.totalUsdc) || 0;
  const consumed = Number(pool.consumedUsdc) || 0;
  if (total <= 0) return 0;
  return clampPct((consumed / total) * 100);
}

export function getRemainingUsdc(pool: LeveragePrizePool): string {
  const total = Number(pool.totalUsdc) || 0;
  const consumed = Number(pool.consumedUsdc) || 0;
  return String(Math.max(0, total - consumed));
}

export function isTierEqual(left: LeverageTier, right: LeverageTier) {
  return (
    left.tierIndex === right.tierIndex &&
    String(left.volumeThresholdUsdc) === String(right.volumeThresholdUsdc) &&
    String(left.rewardUsdc) === String(right.rewardUsdc)
  );
}

export function sortTiers(tiers: LeverageTier[]) {
  return [...tiers].sort((a, b) => a.tierIndex - b.tierIndex);
}

export type CampaignStatus = 'online' | 'paused' | 'offline';

export function computeCampaignStatus(
  enabled: boolean,
  startMs: number,
  endMs: number,
  now: number = Date.now()
): CampaignStatus {
  if (!startMs || !endMs || now < startMs || now > endMs) return 'offline';
  return enabled ? 'online' : 'paused';
}

export function getCampaignStatusColor(
  status: CampaignStatus
): 'success' | 'warning' | 'default' {
  if (status === 'online') return 'success';
  if (status === 'paused') return 'warning';
  return 'default';
}

export function normalizeNumberString(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '0';
  const raw = String(value).trim().replace(/[^\d.]/g, '');
  if (!raw || raw === '.') return '0';
  const firstDot = raw.indexOf('.');
  const single =
    firstDot === -1
      ? raw
      : raw.slice(0, firstDot + 1) + raw.slice(firstDot + 1).replace(/\./g, '');
  const trimmed = single.endsWith('.') ? single.slice(0, -1) : single;
  const padded = trimmed.startsWith('.') ? `0${trimmed}` : trimmed;
  return padded || '0';
}

export function getErrorMessage(error: unknown): string {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return String(error);
}
